import React, { useState, useEffect, useMemo, useCallback } from 'react';
import DateInput from './DateInput';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  type ColDef,
  type ValueFormatterParams,
  ModuleRegistry,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { useGridCopyMenu } from './gridCommon';
import StrategySlotGuide from './StrategySlotGuide';
import { callAxiosGet } from '../tmslib/src/util/axiosutils';
import { useMessageState } from '../tmslib/src/context/MessageContext';
import { getMainAxios } from '../utils/util';
import { useAuth } from '../context/AuthContext';
import {
  page, card, cardGrid, cardSub, titleRow, gridTitle, gridTitleSub, gridHint, toolbarLabel,
  btnPrimary, selectSm, emptyMsg, listRow, listRowHeader, listRowDetail,
  signColor, won, pct, colors, agGridBox,
} from '../styles/fdsTheme';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

/**
 * 전략별 성과 — 한 계좌 안의 전략 슬롯(kispfstrat)별 주문·성과와 계좌 통합 성과.
 *
 * 계좌는 상단 전역 선택(useAuth().selectedPfId)을 따른다.
 *   /api/v1/pfstrat/summary  슬롯별·통합 성과 + 일별 손익 (PfStratEndpoints)
 *   /api/v1/pfstrat/orders   일자별 슬롯 주문
 *
 * 성과는 슬롯 주문의 체결을 평균단가로 누적한 값이고 수수료는 추정치다.
 * 계좌 실손익(증권사 기준)은 '포트폴리오복기'에서 본다.
 * 설계: docs/design/전략슬롯-멀티전략-성과측정.md
 */

interface Holding { prodId: string; prodNm?: string; pos: number; avg: number; px: number; unrealized: number; }

interface SlotRow {
  id: number;
  name: string;
  code?: string;
  stratName?: string;
  type?: string;
  allocAmt: number;
  enabled: boolean;
  startD?: string;
  params?: string | Record<string, unknown> | null;
  realized: number | null;
  unrealized: number | null;
  fee: number | null;
  tax?: number | null;          // 추정 거래세 (매도 0.20%, ETF·ETN 면제)
  preTax?: number | null;       // 비용 전 손익 = 실현 + 평가
  total: number | null;         // 순손익 = 실현 + 평가 − 수수료 − 세금
  retPct: number | null;
  buyAmt: number;
  sellAmt: number;
  nFill: number;
  nSell: number;
  nWin: number;
  winRate: number | null;
  mdd: number;
  mddPct: number | null;
  holdings: Holding[];
  isTotal?: boolean;
}

/** 계좌 = 원계좌 실손익. 슬롯 합이 아니라 순자산(NAV) 변화다. */
interface Account {
  total: number | null;        // 기간 실손익
  retPct: number | null;       // 초기금액 대비
  initAmt: number;
  allocSum: number;            // 슬롯 배정 합
  navStart: number | null;
  navEnd: number | null;
  cumPnl: number | null;       // 개설 이후 누적 (NAV − 초기금액)
  slotSum: number;             // 슬롯 손익 합
  slotFee?: number;            // 슬롯 추정 수수료 합
  slotTax?: number;            // 슬롯 추정 거래세 합
  other: number | null;        // 전략 외 = 실손익 − 슬롯 합 (이월·수동·ENS·실제 비용 차이)
  buyAmt: number; sellAmt: number; nFill: number;
}

interface DailyRow {
  d: string;
  total: number | null;        // 계좌 실손익 (NAV 변화)
  cumTotal: number | null;
  nav: number | null;
  slotSum: number;
  nFill: number;
  bySlot: Record<string, number>;
}

interface SummaryRes { pfId: number; days: string[]; slots: SlotRow[]; account: Account; daily: DailyRow[]; slotPfIds?: number[]; }

interface OrderRow {
  id: number;
  slotId?: number;
  slotName?: string;
  prodId: string;
  prodNm?: string;
  side: string;
  qty2ord: number;
  cumQty: number;
  avgPx?: number;
  amt: number;
  state: number;
  stateNm: string;
  hm: string;
  msg?: string;
  errMsg?: string;
}

/** 로컬(KST) 기준 오늘 — toISOString 은 UTC 라 09시 전엔 어제가 된다 */
const localToday = () => {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
};

const wonFmt = (p: ValueFormatterParams) => won(p.value);
const pctFmt = (p: ValueFormatterParams) => pct(p.value);
const signStyle = (p: { value?: number | null }) => ({ textAlign: 'right', color: signColor(p.value) });
const right = { textAlign: 'right' } as const;

const StrategySlotReview: React.FC = () => {
  const { msgBox: m, logger } = useMessageState();
  const axiosInstance = getMainAxios();
  const { selectedPfId } = useAuth();
  const pfId = selectedPfId;
  const slotCopy = useGridCopyMenu({});
  const orderCopy = useGridCopyMenu({});

  const [days, setDays] = useState(10);
  const [endD, setEndD] = useState(localToday());
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState<SummaryRes | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [slotId, setSlotId] = useState<number | null>(null);   // 슬롯 클릭 → 주문·일별 필터
  const [openD, setOpenD] = useState<string | null>(null);
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [ordLoading, setOrdLoading] = useState(false);

  const fetchAll = useCallback(() => {
    if (!pfId) return;
    setLoading(true);
    setErr(null);
    callAxiosGet({
      axiosInstance, m, logger,
      url: '/api/v1/pfstrat/summary',
      params: { pfId, endD, days },
      onSuccess: (data) => {
        setRes(data as SummaryRes);
        setLoading(false);
      },
      onError: (errMsgs: string[]) => {
        setRes(null);
        setLoading(false);
        setErr(errMsgs?.join(' / ') || '조회 실패');
      },
    });
  }, [axiosInstance, m, logger, pfId, endD, days]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // 펼친 날의 주문
  useEffect(() => {
    if (!pfId || !openD) { setOrders([]); return; }
    setOrdLoading(true);
    axiosInstance
      .get('/api/v1/pfstrat/orders', { params: { pfId, d: openD, slotId: slotId ?? undefined } })
      .then(r => setOrders(r.data as OrderRow[]))
      .catch(() => setOrders([]))
      .finally(() => setOrdLoading(false));
  }, [axiosInstance, pfId, openD, slotId]);

  const slots = res?.slots ?? [];
  const slotNm = useMemo(() => new Map(slots.map(s => [String(s.id), s.name])), [slots]);
  const selected = slots.find(s => s.id === slotId);

  // 표 맨 아래 — 계좌(원계좌 실손익). 배정자금 칸에 초기금액을 넣어 슬롯 배정과 맞춰 보게 한다.
  const totalRow = useMemo<SlotRow[]>(() => {
    const a = res?.account;
    if (!a) return [];
    return [{
      id: -1, name: '계좌 (실손익)', stratName: 'NAV 변화 기준', allocAmt: a.initAmt, enabled: true,
      realized: null, unrealized: null, fee: null, tax: null, preTax: null, total: a.total, retPct: a.retPct,
      buyAmt: a.buyAmt, sellAmt: a.sellAmt, nFill: a.nFill, nSell: 0, nWin: 0,
      winRate: null, mdd: 0, mddPct: null, holdings: [], isTotal: true,
    }];
  }, [res]);

  const slotCols = useMemo<ColDef<SlotRow>[]>(() => [
    { field: 'name', headerName: '슬롯', width: 120, pinned: 'left' },
    { field: 'stratName', headerName: '전략', width: 120 },
    { field: 'allocAmt', headerName: '배정자금', width: 100, valueFormatter: wonFmt, cellStyle: right,
      headerTooltip: '슬롯 = 배정 자금 · 계좌 = 초기금액' },
    { field: 'total', headerName: '손익금액', width: 100, valueFormatter: wonFmt, cellStyle: signStyle as any,
      headerTooltip: '순손익 = 실현 + 평가 − 수수료 − 세금 (계좌는 NAV 변화)' },
    { field: 'retPct', headerName: '수익률', width: 80, valueFormatter: pctFmt, cellStyle: signStyle as any,
      headerTooltip: '배정자금 대비 (계좌는 초기금액 대비)' },
    { field: 'realized', headerName: '실현', width: 95, valueFormatter: wonFmt, cellStyle: signStyle as any },
    { field: 'unrealized', headerName: '평가', width: 90, valueFormatter: wonFmt, cellStyle: signStyle as any },
    { field: 'preTax', headerName: '비용전손익', width: 95, valueFormatter: wonFmt, cellStyle: signStyle as any,
      headerTooltip: '실현 + 평가 (수수료·세금 빼기 전)' },
    { field: 'fee', headerName: '수수료(추정)', width: 95, valueFormatter: (p) => (p.value == null ? '-' : won(-p.value)),
      cellStyle: { textAlign: 'right', color: colors.down }, headerTooltip: '매수·매도 각 0.015%' },
    { field: 'tax', headerName: '세금(추정)', width: 90, valueFormatter: (p) => (p.value == null ? '-' : won(-p.value)),
      cellStyle: { textAlign: 'right', color: colors.down }, headerTooltip: '증권거래세 매도대금 0.20% (ETF·ETN 면제)' },
    { field: 'nFill', headerName: '체결', width: 65, cellStyle: right },
    { field: 'nSell', headerName: '청산', width: 65, cellStyle: right,
      valueFormatter: (p) => (p.data?.isTotal ? '-' : String(p.value ?? 0)) },
    { field: 'winRate', headerName: '승률', width: 70, cellStyle: right,
      valueFormatter: (p) => (p.value == null ? '-' : `${(p.value as number).toFixed(0)}%`) },
    { field: 'mddPct', headerName: 'MDD', width: 75, valueFormatter: pctFmt, cellStyle: signStyle as any,
      headerTooltip: '누적손익 고점 대비 최대 하락 (배정자금 대비)' },
    { field: 'buyAmt', headerName: '매수금액', width: 105, valueFormatter: wonFmt, cellStyle: right },
    { field: 'sellAmt', headerName: '매도금액', width: 105, valueFormatter: wonFmt, cellStyle: right },
    { headerName: '보유', width: 160,
      valueGetter: (p) => (p.data?.holdings ?? []).map(h => `${h.prodNm || h.prodId} ${h.pos}`).join(', ') },
    { field: 'startD', headerName: '시작일', width: 95, cellStyle: { textAlign: 'center' } },
  ], []);

  const orderCols = useMemo<ColDef<OrderRow>[]>(() => [
    { field: 'hm', headerName: '시각', width: 80, cellStyle: { textAlign: 'center' } },
    { field: 'slotName', headerName: '슬롯', width: 110 },
    { field: 'prodNm', headerName: '종목', width: 150, valueGetter: (p) => p.data?.prodNm || p.data?.prodId },
    { field: 'side', headerName: '구분', width: 65,
      cellStyle: (p) => ({
        textAlign: 'center', fontWeight: 600,
        color: p.value === '매수' ? colors.up : colors.down,
        backgroundColor: p.value === '매수' ? colors.buyBg : colors.sellBg,
      }) },
    { field: 'qty2ord', headerName: '주문', width: 70, cellStyle: right },
    { field: 'cumQty', headerName: '체결', width: 70, cellStyle: right },
    { field: 'avgPx', headerName: '체결가', width: 85, valueFormatter: wonFmt, cellStyle: right },
    { field: 'amt', headerName: '체결금액', width: 100, valueFormatter: wonFmt, cellStyle: right },
    { field: 'stateNm', headerName: '상태', width: 80, cellStyle: { textAlign: 'center' } },
    { field: 'msg', headerName: '메시지', flex: 1, minWidth: 220 },
    { field: 'errMsg', headerName: '오류', width: 160, cellStyle: { color: colors.down } },
  ], []);

  const defaultColDef = useMemo<ColDef>(() => ({
    minWidth: 55, resizable: true, sortable: true, filter: false,
    cellStyle: { fontSize: '12px' },
  }), []);

  // 슬롯별 누적 손익 추이
  const chartOptions = useMemo<Highcharts.Options>(() => {
    const daily = res?.daily ?? [];
    const shown = slots.filter(s => s.nFill > 0 && (slotId == null || s.id === slotId));
    const series: Highcharts.SeriesOptionsType[] = shown.map(s => {
      let cum = 0;
      return {
        type: 'line', name: s.name,
        data: daily.map(r => { cum += r.bySlot[String(s.id)] ?? 0; return [new Date(r.d).getTime(), Math.round(cum)]; }),
      };
    });
    if (slotId == null && daily.length > 0)
      series.push({
        type: 'line', name: '계좌 실손익', dashStyle: 'ShortDash', color: colors.flat, lineWidth: 2,
        data: daily.map(r => [new Date(r.d).getTime(), r.cumTotal == null ? null : Math.round(r.cumTotal)]),
      });
    return {
      chart: { height: 260, style: { fontSize: '11px' } },
      title: { text: undefined },
      credits: { enabled: false },
      xAxis: { type: 'datetime', labels: { format: '{value:%m-%d}' } },
      yAxis: { title: { text: undefined }, labels: { format: '{value:,.0f}' },
        plotLines: [{ value: 0, color: colors.divider, width: 1 }] },
      tooltip: { shared: true, xDateFormat: '%Y-%m-%d', valueSuffix: '원' },
      legend: { itemStyle: { fontSize: '11px', fontWeight: '500' } },
      series,
    };
  }, [res, slots, slotId]);

  const dailyDesc = useMemo(() => [...(res?.daily ?? [])].reverse(), [res]);
  const acc = res?.account;

  return (
    <div style={page}>
      {/* 조회 조건 */}
      <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <span style={toolbarLabel}>
          전략별 성과 {loading ? '(조회 중...)' : `— 계좌 ${pfId ?? '-'} · 최근 ${res?.days.length ?? 0}영업일`}
          {!loading && acc && (
            <span style={{ marginLeft: 8, color: signColor(acc.total), fontWeight: 600 }}>
              계좌 실손익 {won(acc.total)}원 ({pct(acc.retPct)})
            </span>
          )}
        </span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={days} onChange={e => setDays(Number(e.target.value))} style={selectSm}>
            {[10, 20, 30, 60].map(n => <option key={n} value={n}>최근 {n}일</option>)}
          </select>
          <DateInput value={endD} onChange={setEndD} style={{ fontSize: '12px' }} />
          <button style={btnPrimary} onClick={fetchAll}>조회</button>
        </div>
      </div>

      {/* 슬롯 설명 — 전략 규칙·파라미터·공통 운용 규칙 */}
      <StrategySlotGuide slots={slots} selectedId={slotId} onSelect={setSlotId} />

      {/* 슬롯별 성과 + 계좌 통합 */}
      <div style={cardGrid}>
        <div style={titleRow}>
          <h3 style={gridTitle}>
            슬롯별 성과
            <span style={gridHint}>
              (클릭: 그 슬롯만 보기 · 슬롯 = 체결 기준 평균단가·수수료 추정 · 맨 아래 = 원계좌 실손익)
            </span>
          </h3>
          <span style={{ color: colors.title, fontSize: '12px', fontWeight: 500 }}>
            {selected
              ? <>선택: <b>{selected.name}</b>{' '}
                  <a style={{ color: colors.muted, cursor: 'pointer' }} onClick={() => setSlotId(null)}>[전체 보기]</a></>
              : `총 ${slots.length}개`}
          </span>
        </div>
        {err && <p style={{ ...emptyMsg, color: colors.down }}>성과 조회 오류: {err}</p>}
        {!pfId && !loading && <p style={emptyMsg}>상단에서 계좌를 선택하세요.</p>}
        {pfId && !err && !loading && slots.length === 0
          ? (
            <p style={emptyMsg}>
              계좌 {pfId} 에는 전략 슬롯이 없습니다.
              {(res?.slotPfIds?.length ?? 0) > 0
                ? <> 슬롯이 있는 계좌: <b style={{ color: colors.title }}>{res!.slotPfIds!.join(', ')}</b> — 상단에서 계좌를 바꾸세요.</>
                : ' (kispfstrat 등록 필요)'}
            </p>
          )
          : slots.length > 0 && acc && (
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '12px', color: colors.muted, marginBottom: 10 }}>
              <span>초기금액 <b style={{ color: colors.title }}>{won(acc.initAmt)}</b>
                {' '}= 슬롯 {slots.filter(s => s.enabled).length}개 배정 {won(acc.allocSum)}</span>
              <span>계좌 실손익 <b style={{ color: signColor(acc.total) }}>{won(acc.total)}</b></span>
              <span>슬롯 합 <b style={{ color: signColor(acc.slotSum) }}>{won(acc.slotSum)}</b>
                {(acc.slotTax != null || acc.slotFee != null) && (
                  <span title="슬롯 추정 비용 — 이미 슬롯 손익에서 뺐다">
                    {' '}(세금 {won(-(acc.slotTax ?? 0))} · 수수료 {won(-(acc.slotFee ?? 0))})
                  </span>
                )}</span>
              <span title="계좌 실손익 − 슬롯 합: 이월 보유·수동 주문·ENS·실제 수수료와 추정치 차이">
                전략 외 <b style={{ color: signColor(acc.other) }}>{won(acc.other)}</b></span>
              <span>NAV <b style={{ color: colors.body }}>{won(acc.navEnd)}</b>
                {' '}(개설 이후 <b style={{ color: signColor(acc.cumPnl) }}>{won(acc.cumPnl)}</b>)</span>
            </div>
          )}
        {slots.length > 0 && (
            <div className="ag-theme-quartz" style={agGridBox(Math.min(80 + (slots.length + 1) * 30, 420))}>
              <AgGridReact {...slotCopy.gridProps}
                rowData={slots}
                pinnedBottomRowData={totalRow}
                columnDefs={slotCols}
                defaultColDef={defaultColDef}
                animateRows={false}
                onRowClicked={(e) => {
                  if (!e.data || e.data.isTotal) return;
                  setSlotId(slotId === e.data.id ? null : e.data.id);
                }}
                getRowStyle={(p) => p.data?.isTotal
                  ? { fontWeight: 600, backgroundColor: colors.agHeader }
                  : p.data?.id === slotId ? { backgroundColor: '#dbeafe', fontWeight: 600 } : undefined}
              />
            </div>
          )}
        {slotCopy.menu}
      </div>

      {/* 누적 손익 추이 */}
      <div style={cardGrid}>
        <div style={titleRow}>
          <h3 style={gridTitle}>
            누적 손익 추이 <span style={gridHint}>(슬롯별 · 점선 = 계좌 통합)</span>
          </h3>
        </div>
        {(res?.daily.length ?? 0) > 0 && slots.some(s => s.nFill > 0)
          ? <HighchartsReact highcharts={Highcharts} options={chartOptions} />
          : <p style={emptyMsg}>체결이 있는 슬롯이 없습니다.</p>}
      </div>

      {/* 일별 — 게시판 */}
      <div style={card}>
        <div style={titleRow}>
          <h3 style={gridTitle}>
            일별 손익 <span style={gridHint}>(클릭: 그날 슬롯별 손익 · 주문 펼치기{selected ? ` — ${selected.name} 만` : ''})</span>
          </h3>
          <span style={{ color: colors.title, fontSize: '12px', fontWeight: 500 }}>총 {dailyDesc.length}일</span>
        </div>

        {!loading && dailyDesc.length === 0 && <p style={emptyMsg}>해당 기간 기록이 없습니다.</p>}

        {dailyDesc.map(r => {
          const open = openD === r.d;
          const pnl = slotId == null ? (r.total ?? r.slotSum) : (r.bySlot[String(slotId)] ?? 0);
          const other = r.total == null ? null : r.total - r.slotSum;
          const parts = Object.entries(r.bySlot)
            .filter(([, v]) => Math.abs(v) >= 1)
            .sort((a, b) => b[1] - a[1]);
          const best = parts[0];
          const worst = parts[parts.length - 1];
          return (
            <div key={r.d} style={listRow(r.nFill > 0 || Math.abs(pnl) >= 1 ? signColor(pnl) : undefined)}>
              <div style={listRowHeader} onClick={() => setOpenD(open ? null : r.d)}>
                <span style={{ color: colors.hint, width: 12 }}>{open ? '▾' : '▸'}</span>
                <b style={{ color: colors.title, width: 88 }}>{r.d}</b>
                <b style={{ color: signColor(pnl), width: 108, textAlign: 'right' }}>{won(pnl)}원</b>
                <span style={{ color: colors.body, flex: 1, minWidth: 220 }}>
                  {r.nFill > 0 ? `체결 ${r.nFill}건` : <span style={{ color: colors.hint }}>매매 없음</span>}
                  {slotId == null && best && best[1] > 0 && (
                    <span>{' · '}최고 {slotNm.get(best[0]) ?? best[0]} <span style={{ color: colors.up }}>{won(best[1])}</span></span>
                  )}
                  {slotId == null && worst && worst[1] < 0 && (
                    <span>{' · '}최저 {slotNm.get(worst[0]) ?? worst[0]} <span style={{ color: colors.down }}>{won(worst[1])}</span></span>
                  )}
                </span>
                <span style={{ color: colors.muted, fontSize: '11px' }}>
                  {slotId == null && r.total == null ? '잔고 없음 · ' : ''}누적 {won(r.cumTotal)}
                </span>
              </div>

              {open && (
                <div style={listRowDetail}>
                  {/* 그날 슬롯별 손익 */}
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '12px', marginBottom: 10 }}>
                    {parts.length === 0
                      ? <span style={{ color: colors.hint }}>손익 변동 없음</span>
                      : parts.map(([sid, v]) => (
                        <span key={sid} style={{ color: colors.muted }}>
                          {slotNm.get(sid) ?? `#${sid}`} <b style={{ color: signColor(v) }}>{won(v)}</b>
                        </span>
                      ))}
                    {other != null && Math.abs(other) >= 1 && (
                      <span style={{ color: colors.muted }} title="계좌 실손익 − 슬롯 합: 이월 보유·수동 주문·ENS·실제 비용 차이">
                        전략 외 <b style={{ color: signColor(other) }}>{won(other)}</b>
                      </span>
                    )}
                    {r.total != null && (
                      <span style={{ color: colors.muted }}>
                        계좌 실손익 <b style={{ color: signColor(r.total) }}>{won(r.total)}</b>
                      </span>
                    )}
                  </div>

                  {/* 그날 주문 */}
                  <div style={{ ...cardSub, marginBottom: 0 }}>
                    <div style={titleRow}>
                      <h3 style={gridTitleSub}>
                        주문 {ordLoading ? '(조회 중...)' : ''}
                        <span style={gridHint}>{selected ? `${selected.name} 만` : '전체 슬롯'}</span>
                      </h3>
                      <span style={{ color: colors.muted, fontSize: '11px' }}>총 {orders.length}건</span>
                    </div>
                    {orders.length === 0 && !ordLoading
                      ? <p style={emptyMsg}>주문이 없습니다.</p>
                      : (
                        <div className="ag-theme-quartz" style={agGridBox(Math.min(80 + orders.length * 30, 360))}>
                          <AgGridReact {...orderCopy.gridProps}
                            rowData={orders}
                            columnDefs={orderCols}
                            defaultColDef={defaultColDef}
                            animateRows={false}
                          />
                        </div>
                      )}
                    {orderCopy.menu}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StrategySlotReview;
